import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import ReactGA from 'react-ga';

import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';

import { useClient, QueryResponse } from 'react-fetching-library';

import AuthContext from './AuthContext';
import firebase from '../../../common/firebase';
import useSentry from '../../../common/libs/sentry/useSentry';
import { K_USER_LEVEL_VERIFIED_EMAIL, K_USER_LEVEL_ANONYMOUS } from '../../shared/constants';
import useTracking from '../../../common/utils/tracking/useTracking';

export const K_USER_TOKEN_COOKIE_KEY = 'ut';

export function constructUserFromFirebaseUser(firebaseUser) {
  if (!firebaseUser) return null;
  const emailVerified = firebaseUser.email_verified || firebaseUser.emailVerified;
  return {
    email: firebaseUser.email,
    emailVerified: emailVerified,
    kyc_level: emailVerified ? K_USER_LEVEL_VERIFIED_EMAIL : K_USER_LEVEL_ANONYMOUS
  };
}

export default function AuthProvider({ user: initialUser, children }) {
  const [user, setUser] = useState(initialUser);
  const tracking = useTracking();
  const { query } = useClient();
  const { Sentry } = useSentry();

  function handleRefetch() {
    const fUser = firebase.auth().currentUser;
    if (!isEmpty(fUser)) {
      return firebase
        .auth()
        .currentUser.getIdToken(true)
        .then(token => {
          Cookies.set(K_USER_TOKEN_COOKIE_KEY, token);
          fetchUserDetail(fUser, token);
        });
    } else {
      logoutUser();
    }
  }

  useEffect(() => {
    return firebase.auth().onAuthStateChanged(() => {
      return handleRefetch();
    });
  }, []);

  function logoutUser() {
    if (!user) return null;
    Cookies.remove(K_USER_TOKEN_COOKIE_KEY);
    setUser(undefined);
    firebase.auth().signOut();
    try {
      (window as any).fcWidget.user.clear();
    } catch (e) {}
  }

  async function fetchUserDetail(firebaseUser, firebaseToken) {
    // this function triggers after get firebase user
    const result: QueryResponse<any> = await query({
      url: '/v3/user/individual',
      method: 'get'
    });
    const { error, payload: _payload } = result;
    if (!error) {
      const { payload } = _payload;
      if (!isEmpty(payload)) {
        // there's a user from backend
        payload.emailVerified = true;
        // payload.kyc_level = K_USER_LEVEL_BANK_ACCOUNT;
        if (
          payload &&
          !isEqual(
            omit(payload, ['url_id_image_uploaded', 'url_selfie_image_uploaded']),
            omit(user, ['url_id_image_uploaded', 'url_selfie_image_uploaded'])
          ) // omit url_id_image_uploaded & url_selfie_image_uploaded because there's secure token in url
        ) {
          setUser(payload);
        }
      }
    } else {
      // user haven't registered from backend, but already registered to firebase
      const payload = constructUserFromFirebaseUser(firebaseUser);
      if (!isEqual(payload, user)) {
        setUser(payload);
      }
    }
  }

  if (user && user.email) {
    if (Sentry) {
      Sentry.configureScope(scope => {
        scope.setUser({
          id: user.id,
          email: user.email,
          emailVerified: user.emailVerified,
          fullname: user.fullname,
          kyc_level: user.kyc_level
        });
      });
    }
    ReactGA.set({ userId: user.id });
    tracking.setUser(user);
  }
  return (
    <AuthContext.Provider
      value={{ user, logout: logoutUser, isLoggedIn: !isEmpty(user), refetch: handleRefetch }}
    >
      {children}
    </AuthContext.Provider>
  );
}
